import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, CircleMarker, Marker, Popup, Tooltip, Polyline, useMap, useMapEvents } from 'react-leaflet';
import OsmTiles from '../../components/OsmTiles';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { collection, onSnapshot, query, where, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { baixarIndiceClientes } from '../../services/clientsIndex';
import { LiveLocation, RouteStop, User, Client, clientDisplayName, isFullAccess } from '../../types';
import { haversine } from '../../utils/route';
import { coordenadaConfiavel, temCoordenada } from '../../utils/navegacaoCliente';
import { useAccessScope } from '../../hooks/useAccessScope';
import { useAuth } from '../../contexts/AuthContext';
import { escHtml } from '../../utils/escapeHtml';
import ClientFormModal from '../../components/ClientFormModal';
import 'leaflet/dist/leaflet.css';

// Status do cliente (mesmas cores da Roteirização) para os pinos opcionais.
type StatusKey = 'sem_visita_rede' | 'sem_visita' | 'com_visita' | 'com_venda';
const CLIENT_STATUS: Record<StatusKey, { label: string; color: string }> = {
  sem_visita_rede: { label: 'Sem visita (rede)', color: '#2f80ed' },
  sem_visita: { label: 'Sem visita (sem rede)', color: '#475569' },
  com_visita: { label: 'Com visita', color: '#f5821f' },
  com_venda: { label: 'Com venda', color: '#22c55e' },
};
const toDateAny = (v: any): Date | null => (v?.toDate ? v.toDate() : v instanceof Date ? v : null);
function clientStatus(c: Client): StatusKey {
  if (toDateAny(c.sankhya?.lastPurchaseAt)) return 'com_venda';
  if (toDateAny(c.lastCheckinAt)) return 'com_visita';
  return c.sankhya?.rede != null && c.sankhya?.rede !== '' ? 'sem_visita_rede' : 'sem_visita';
}
const clientPinCache: Record<string, L.DivIcon> = {};
function clientPin(color: string): L.DivIcon {
  if (clientPinCache[color]) return clientPinCache[color];
  const html = `<svg width="24" height="33" viewBox="0 0 26 36" xmlns="http://www.w3.org/2000/svg"><path d="M13 0C5.8 0 0 5.8 0 13c0 9.2 13 23 13 23s13-13.8 13-23C26 5.8 20.2 0 13 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/><circle cx="13" cy="13" r="5" fill="#fff"/></svg>`;
  const icon = L.divIcon({ className: 'client-pin', html, iconSize: [24, 33], iconAnchor: [12, 33], popupAnchor: [0, -30] });
  clientPinCache[color] = icon;
  return icon;
}
// Camada de pinos de cliente com clustering (imperativo via markercluster).
function ClientPins({ clients, onSelect }: { clients: Client[]; onSelect: (c: Client) => void }) {
  const map = useMap();
  useEffect(() => {
    // chunkedLoading DESLIGADO de propósito: em lote assíncrono, se `clients` mudar e o cleanup remover
    // o cluster antes do lote rodar, ele estoura em `_map.getMinZoom()` (null). Síncrono é seguro aqui.
    const cluster = (L as any).markerClusterGroup({ chunkedLoading: false, maxClusterRadius: 55, showCoverageOnHover: false, spiderfyOnMaxZoom: true, spiderfyDistanceMultiplier: 1.6 });
    clients.forEach((c) => {
      if (!c.location?.latitude || !c.location?.longitude) return;
      const m = L.marker([c.location.latitude, c.location.longitude], { icon: clientPin(CLIENT_STATUS[clientStatus(c)].color) });
      m.on('click', () => onSelect(c));
      const estrelas = typeof c.estrelas === 'number' && c.estrelas > 0 ? ' ' + '★'.repeat(Math.min(3, Math.trunc(c.estrelas) || 0)) : '';
      m.bindTooltip(escHtml(clientDisplayName(c) + estrelas), { direction: 'top' }); // nome vem do ERP → escapar (bindTooltip é HTML)
      cluster.addLayer(m);
    });
    map.addLayer(cluster);
    return () => { map.removeLayer(cluster); };
  }, [clients, map, onSelect]);
  return null;
}

// Marcador do vendedor que DESLIZA até a posição nova em vez de saltar.
// Duas coisas estavam erradas no `<CircleMarker center={[l.lat, l.lng]}>` anterior: (a) nenhuma
// animação — o ponto teleportava a cada atualização; e (b) o array literal cria uma REFERÊNCIA nova a
// cada render, e o react-leaflet compara `center` por referência, então ele chamava `setLatLng` em
// TODO re-render do pai (inclusive no tick de 60s que só recalcula o texto "há X min"), atropelando
// qualquer animação. Aqui o `center` é congelado na montagem e o movimento passa a ser imperativo.
function AnimatedCircleMarker({ lat, lng, children, ...rest }: any) {
  const map = useMap();
  const ref = useRef<L.CircleMarker | null>(null);
  const inicial = useRef<[number, number]>([lat, lng]); // NUNCA muda de referência — de propósito
  const raf = useRef<number | null>(null);
  const ultimoAt = useRef(0);

  useEffect(() => {
    const mk = ref.current;
    if (!mk) return;
    const de = mk.getLatLng();
    const agora = Date.now();
    const cad = ultimoAt.current ? agora - ultimoAt.current : 1000; // cadência MEDIDA, não presumida
    ultimoAt.current = agora;
    if (raf.current) cancelAnimationFrame(raf.current);
    const metros = map.distance(de, L.latLng(lat, lng));
    // Salto grande / lacuna longa / parado: teleporta (deslizar desenharia uma reta atravessando
    // quarteirões que o vendedor não percorreu).
    if (metros > 300 || cad > 60000 || metros < 0.5) { mk.setLatLng([lat, lng]); return; }
    const dur = Math.max(300, Math.min(12000, cad));
    const t0 = agora, dLat = lat - de.lat, dLng = lng - de.lng;
    const passo = () => {
      const k = Math.min(1, (Date.now() - t0) / dur); // LINEAR: ease faria parecer que ele freou
      mk.setLatLng([de.lat + dLat * k, de.lng + dLng * k]);
      raf.current = k < 1 ? requestAnimationFrame(passo) : null;
    };
    raf.current = requestAnimationFrame(passo);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [lat, lng, map]);

  // Tooltip e Popup filhos seguem sozinhos: o Leaflet os move pelo evento `move` do marcador.
  return <CircleMarker ref={ref} center={inicial.current} {...rest}>{children}</CircleMarker>;
}

const AVG_KMH = 30; // fallback: velocidade média urbana se não houver duração do ORS

// Minutos até o destino. Preciso quando há a duração real do ORS (escala pela fração restante
// do percurso); senão estima por distância restante ÷ velocidade média.
function etaMin(l: LiveLocation): number | null {
  if (l.lat == null || l.lng == null || l.destLat == null || l.destLng == null) return null;
  const route = (l.suggestedRoute || []);
  let remaining: number; // metros restantes ao longo da rota (ou reta×fator)
  let total = 0;          // metros totais da rota sugerida (p/ a fração)
  if (route.length > 1) {
    let nearest = 0, best = Infinity;
    for (let i = 0; i < route.length; i++) {
      const d = haversine({ lat: l.lat, lng: l.lng }, { lat: route[i].lat, lng: route[i].lng });
      if (d < best) { best = d; nearest = i; }
    }
    remaining = best;
    for (let i = 0; i < route.length - 1; i++) {
      const seg = haversine({ lat: route[i].lat, lng: route[i].lng }, { lat: route[i + 1].lat, lng: route[i + 1].lng });
      total += seg;
      if (i >= nearest) remaining += seg;
    }
  } else {
    remaining = haversine({ lat: l.lat, lng: l.lng }, { lat: l.destLat, lng: l.destLng }) * 1.35;
  }
  // ETA preciso: duração ORS × (restante / total). Senão, distância ÷ velocidade média.
  if (l.suggestedDurationSeconds != null && total > 0) {
    const frac = Math.max(0, Math.min(1, remaining / total));
    return Math.round((l.suggestedDurationSeconds * frac) / 60);
  }
  return Math.round(remaining / 1000 / AVG_KMH * 60);
}
// ETA legível: <1 min "chegando"; <60 min "~45 min"; >=60 min "~1h 23min" (ou "~2h").
const fmtEta = (m: number | null): string => {
  if (m == null) return '';
  if (m < 1) return 'chegando';
  if (m < 60) return `~${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `~${h}h ${rem}min` : `~${h}h`;
};

// Distância restante até o cliente (metros): ao longo da rota sugerida, ou reta×1.35.
function remainingMeters(l: LiveLocation): number | null {
  if (l.lat == null || l.lng == null || l.destLat == null || l.destLng == null) return null;
  const route = l.suggestedRoute || [];
  if (route.length > 1) {
    let nearest = 0, best = Infinity;
    for (let i = 0; i < route.length; i++) {
      const d = haversine({ lat: l.lat, lng: l.lng }, { lat: route[i].lat, lng: route[i].lng });
      if (d < best) { best = d; nearest = i; }
    }
    let rem = best;
    for (let i = nearest; i < route.length - 1; i++) rem += haversine(route[i], route[i + 1]);
    return rem;
  }
  return haversine({ lat: l.lat, lng: l.lng }, { lat: l.destLat, lng: l.destLng }) * 1.35;
}
// Distância da posição atual até a rota sugerida (metros) — p/ detectar desvio.
function desvioMeters(l: LiveLocation): number | null {
  const route = l.suggestedRoute || [];
  if (l.lat == null || l.lng == null || route.length < 2) return null;
  let best = Infinity;
  for (const p of route) { const d = haversine({ lat: l.lat, lng: l.lng }, { lat: p.lat, lng: p.lng }); if (d < best) best = d; }
  return best;
}
// "Parado" se ficou dentro de ~80m da posição atual; retorna há quantos minutos.
function statusMov(l: LiveLocation): { parado: boolean; min: number } | null {
  const trail = l.trail || [];
  if (l.lat == null || l.lng == null || trail.length === 0) return null;
  let since = l.updatedAt.getTime();
  for (let i = trail.length - 1; i >= 0; i--) {
    const p = trail[i];
    if (haversine({ lat: l.lat, lng: l.lng }, { lat: p.lat, lng: p.lng }) > 80) break;
    if (p.t) since = Math.min(since, p.t);
  }
  const min = Math.round((Date.now() - since) / 60000);
  return { parado: min >= 4, min };
}
const fmtKm = (m: number | null): string => (m == null ? '' : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
const fmtHora = (d: Date): string => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
// Duração legível a partir de minutos: "45 min" ou "1h 23min".
const fmtMin = (m: number | null): string => { if (m == null) return ''; if (m < 60) return `${m} min`; const h = Math.floor(m / 60), r = m % 60; return r ? `${h}h ${r}min` : `${h}h`; };
// Endereço curto do cliente para o card do mapa ao vivo.
function fmtEndereco(a?: { street?: string; number?: string; neighborhood?: string; city?: string; state?: string }): string {
  if (!a) return '';
  const ruaNum = [a.street, a.number].filter(Boolean).join(', ');
  const cidadeUf = [a.city, a.state].filter(Boolean).join('/');
  return [ruaNum, a.neighborhood, cidadeUf].filter(Boolean).join(' · ');
}

// Velocidade aproximada (km/h) pelos 2 últimos pontos do trajeto com timestamp.
function speedKmh(l: LiveLocation): number | null {
  const trail = l.trail || [];
  if (trail.length < 2) return null;
  const b = trail[trail.length - 1], a = trail[trail.length - 2];
  if (!a.t || !b.t || b.t <= a.t) return null;
  const dtH = (b.t - a.t) / 3600000;
  const v = (haversine({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }) / 1000) / dtH;
  if (!isFinite(v) || v > 160) return null; // descarta glitch de GPS
  return v;
}
const soDigitos = (s?: string) => (s || '').replace(/\D/g, '');

// Ângulo (graus) de a→b, para a seta de direção.
function bearing(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const y = Math.sin(toRad(b.lng - a.lng)) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) - Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lng - a.lng));
  return (Math.atan2(y, x) * 180) / Math.PI;
}
// Direção de deslocamento pelos 2 últimos pontos do trajeto (null se parado/sem dados).
function headingOf(l: LiveLocation): number | null {
  const t = l.trail || [];
  if (t.length < 2) return null;
  const a = t[t.length - 2], b = t[t.length - 1];
  if (haversine({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }) < 8) return null; // quase parado
  return bearing(a, b);
}
// Quebra a polilinha do trajeto REAL em segmentos, cortando onde dois pontos consecutivos ficam a mais de
// SALTO_TRAJETO_M metros. O GPS em 2º plano fica esparso (Android limita updates; em rodovia o salto vira
// vários km) e ligar reto desenharia uma diagonal FALSA cruzando mata/rio — quebrar mostra só os trechos reais.
const SALTO_TRAJETO_M = 800;
function segmentarTrajeto(pts: [number, number][]): [number, number][][] {
  if (pts.length < 2) return pts.length ? [pts] : [];
  const segs: [number, number][][] = [];
  let cur: [number, number][] = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    if (haversine({ lat: pts[i - 1][0], lng: pts[i - 1][1] }, { lat: pts[i][0], lng: pts[i][1] }) > SALTO_TRAJETO_M) {
      segs.push(cur); cur = [pts[i]];
    } else {
      cur.push(pts[i]);
    }
  }
  segs.push(cur);
  return segs;
}
function arrowIcon(deg: number): L.DivIcon {
  return L.divIcon({
    className: 'heading-arrow',
    html: `<div style="transform:rotate(${deg}deg);font-size:20px;line-height:1;color:#0f766e;text-shadow:0 0 3px #fff,0 0 3px #fff">➤</div>`,
    iconSize: [20, 20], iconAnchor: [10, 10],
  });
}
// Captura o mapa e o zoom atual — usados para abrir marcadores sobrepostos em LEQUE em espaço de PIXEL
// (recomputa a cada zoom para o afastamento na tela ficar constante).
function MapProbe({ onMap, onZoom }: { onMap: (m: L.Map) => void; onZoom: (z: number) => void }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onMap(map); onZoom(map.getZoom()); }, [map, onMap, onZoom]);
  return null;
}

// Centraliza o mapa: no clique (focus, uma vez) e enquanto "segue" um vendedor (follow, contínuo).
function MapController({ focus, follow }: { focus: { pos: [number, number]; n: number } | null; follow: { lat: number; lng: number } | null }) {
  const map = useMap();
  useEffect(() => { if (focus) map.setView(focus.pos, Math.max(map.getZoom(), 15)); }, [focus, map]);
  useEffect(() => { if (follow) map.setView([follow.lat, follow.lng], Math.max(map.getZoom(), 15)); }, [follow?.lat, follow?.lng, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function LiveMapPage() {
  const { user } = useAuth(); // CODVEND do gestor — usado para escopar a consulta de liveLocations
  const scope = useAccessScope();
  const [allLocs, setAllLocs] = useState<LiveLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [mapObj, setMapObj] = useState<L.Map | null>(null);
  const [zoomLvl, setZoomLvl] = useState(11);
  const [routes, setRoutes] = useState<Record<string, RouteStop[]>>({});
  const [phones, setPhones] = useState<Record<string, string>>({});
  const [usersById, setUsersById] = useState<Record<string, User>>({}); // uid -> doc do usuário (role/equipe p/ a camada Clientes)
  const [clientAddr, setClientAddr] = useState<Record<string, string>>({}); // destClientId -> endereço curto
  // destClientId -> coord ATUAL do cadastro + se ela é confiável (`coordenadaConfiavel`, a MESMA regra do
  // "Navegar" do app). null = cadastro sem coordenada.
  const [clientLoc, setClientLoc] = useState<Record<string, { lat: number; lng: number; confiavel: boolean } | null>>({});
  const [focus, setFocus] = useState<{ pos: [number, number]; n: number } | null>(null);
  const [following, setFollowing] = useState<string | null>(null); // sellerId sendo seguido
  const [showClients, setShowClients] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [ficha, setFicha] = useState<Client | null>(null);
  const [matchedTrails, setMatchedTrails] = useState<Record<string, { len: number; lastT?: number; destClientId?: string | null; pts: [number, number][] }>>({});
  const matchedTrailsRef = useRef(matchedTrails);
  useEffect(() => { matchedTrailsRef.current = matchedTrails; }, [matchedTrails]);
  const matchInFlight = useRef<Set<string>>(new Set());
  const [, setTick] = useState(0);
  // Gestor: só os vendedores da hierarquia dele aparecem no mapa ao vivo.
  // Destino: sobrescreve destLat/destLng pela coordenada ATUAL do cadastro (clientLoc). O snapshot gravado
  // no início da navegação fica OBSOLETO se a coordenada do cliente foi corrigida depois — era o bug: o
  // pino aparecia no lugar antigo mesmo com a coord certa. Se divergir >150m, a rota sugerida antiga
  // (que aponta pro ponto velho) também é descartada.
  // ⚠️ E a sobrescrita só vale quando a coordenada do cadastro é CONFIÁVEL (utils/navegacaoCliente.ts,
  // cópia byte a byte da do app). Não confiável ('city' = centro do município, 'street'/'postal', OUTRO
  // município, o reverse não achou, ou `geoNavegavel: false` do servidor) → vale o SNAPSHOT do próprio
  // vendedor, que diz para onde ele está indo DE FATO: o app novo navega pelo endereço em TEXTO e grava
  // destLat/destLng null (fica destino sem coordenada, sem pino) — ou, offline, a coordenada da semente
  // que ele usou; o APK antigo (1.6.9/1.7.0, `main`, sem OTA) navega pela coordenada SEMPRE e grava ela —
  // o pino/ETA aparecem lá porque é para lá que a navegação dele o leva. Zerar esconderia do gestor o
  // trajeto que está acontecendo.
  // Snapshot com 0,0 ou fora da faixa (vazio de importação que um APK antigo gravou) não é lugar: vira
  // destino sem coordenada, e a rota sugerida (aponta para ele) sai junto. Mesma régua do `temCoordenada`.
  const locs = useMemo(() => {
    const scoped = scope.full ? allLocs : allLocs.filter((l) => (scope.sellerIds || []).includes(l.sellerId));
    return scoped.map((l0) => {
      const snapRuim = (l0.destLat != null || l0.destLng != null)
        && !temCoordenada({ location: { latitude: l0.destLat, longitude: l0.destLng } });
      const l = snapRuim ? { ...l0, destLat: null, destLng: null, suggestedRoute: [] } : l0;
      if (!l.destClientId) return l;
      const cur = clientLoc[l.destClientId];
      // Ainda buscando a coord ATUAL → esconde o destino (não mostra o snapshot antigo por um instante,
      // e o enquadramento inicial fica na área do vendedor/coord certa). Some por ~1 leitura.
      if (cur === undefined) return { ...l, destLat: null, destLng: null };
      if (!cur) return l; // cliente sem coordenada no cadastro → mantém o snapshot como fallback
      if (!cur.confiavel) return l; // cadastro não confiável → o snapshot do vendedor (ver acima)
      const snapFar = l.destLat != null && l.destLng != null && haversine(cur, { lat: l.destLat, lng: l.destLng }) > 150; // mesmo limite do app (DEST_MOVED_M)
      return { ...l, destLat: cur.lat, destLng: cur.lng, suggestedRoute: snapFar ? [] : l.suggestedRoute };
    });
  }, [allLocs, scope, clientLoc]);

  // Vendedores no ~mesmo ponto na tela: mantém as bolinhas no lugar, mas SEPARA os nomes (um p/ cima, outro
  // p/ baixo…) alternando a direção do rótulo, p/ os textos não sobreporem. Agrupa por proximidade em PIXEL
  // (no zoom atual — precisa do mapa). Vendedor sozinho fica no padrão ('top').
  const fan = useMemo(() => {
    const dir = new Map<string, 'top' | 'bottom' | 'right' | 'left'>();
    if (!mapObj || locs.length === 0) return { dir };
    const pts = locs.map((l) => mapObj.latLngToLayerPoint([l.lat, l.lng]));
    const gOf = new Array(locs.length).fill(-1);
    const groups: number[][] = [];
    for (let i = 0; i < locs.length; i++) {
      if (gOf[i] !== -1) continue;
      const g = [i]; gOf[i] = groups.length;
      for (let j = i + 1; j < locs.length; j++) {
        if (gOf[j] === -1 && pts[i].distanceTo(pts[j]) < 24) { g.push(j); gOf[j] = groups.length; }
      }
      groups.push(g);
    }
    const DIRS: ('top' | 'bottom' | 'right' | 'left')[] = ['top', 'bottom', 'right', 'left'];
    groups.forEach((g) => {
      if (g.length === 1) return; // sozinho: mantém 'top' (padrão)
      g.forEach((idx, k) => dir.set(locs[idx].sellerId, DIRS[k % DIRS.length]));
    });
    return { dir };
  }, [locs, mapObj, zoomLvl]);

  useEffect(() => {
    // Acesso total lê todas; GESTOR precisa carregar o filtro por hierarquia NA QUERY — a regra só o
    // deixa ler docs com o CODVEND dele em `gestoresCodvends`, e regra não filtra: sem o
    // `array-contains` o Firestore recusaria a consulta inteira e o mapa ficaria vazio.
    const meuCod = user?.codVendSankhya;
    if (!scope.full && meuCod == null) { setAllLocs([]); setLoading(false); return; } // gestor sem CODVEND não tem equipe
    const q = scope.full
      ? query(collection(db, 'liveLocations'), where('active', '==', true))
      : query(collection(db, 'liveLocations'), where('active', '==', true), where('gestoresCodvends', 'array-contains', Number(meuCod)));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAllLocs(snap.docs
          .map((d) => {
            const r = d.data() as any;
            return { ...r, sellerId: d.id, updatedAt: r.updatedAt?.toDate?.() ?? new Date(), startedAt: r.startedAt?.toDate?.() ?? null };
          })
          .filter((l) => typeof l.lat === 'number' && typeof l.lng === 'number') as LiveLocation[]);
        setLoading(false);
      },
      () => setLoading(false)
    );
    // Atualiza o "há X min" a cada 60s
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => { unsub(); clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.full, user?.codVendSankhya]);

  // Telefones (botões Ligar/WhatsApp) + CODVEND de cada usuário (p/ a camada "Clientes" achar a
  // carteira de gestores/supervisores, cujos clientes casam por sankhya.codvend e não por sellerId).
  useEffect(() => {
    getDocs(collection(db, 'users')).then((snap) => {
      const m: Record<string, string> = {};
      const byId: Record<string, User> = {};
      snap.forEach((d) => {
        const u = { id: d.id, ...d.data() } as User;
        if (u.phone) m[d.id] = u.phone;
        byId[d.id] = u; // doc inteiro (role/equipeCodvends/equipeSellerIds) p/ a camada Clientes por hierarquia
      });
      setPhones(m);
      setUsersById(byId);
    }).catch(() => { /* segue sem telefones */ });
  }, []);

  // Camada opcional "Clientes": carteira de CADA pessoa no mapa resolvida pela HIERARQUIA (não pelo
  // codvend próprio): gestor "puro" (ex.: gerente_nacional) não tem cliente ligado direto nele — os
  // clientes são dos VENDEDORES abaixo. Usa a equipe (equipeCodvends/equipeSellerIds já computadas no
  // servidor). Acesso-total no mapa (964) → carteira = base inteira → via ÍNDICE Tier B (1 download c/
  // coordenada). Casa por `sellerId` (app) E `sankhya.codvend` (importado, número E string p/ legado).
  const activeKey = useMemo(() => Array.from(new Set(locs.map((l) => l.sellerId))).sort().join(','), [locs]);
  useEffect(() => {
    if (!showClients) { setClients([]); return; }
    const uids = activeKey ? activeKey.split(',') : []; // SEM cap: os uids só alimentam codSet/sidSet, que já são paginados em lotes de 30 (o cap somia com a carteira da 31ª pessoa no mapa).
    if (!uids.length) { setClients([]); return; }
    // Reúne codvends + sellerIds das EQUIPES das pessoas no mapa (equipe já inclui o próprio gestor).
    const codSet = new Set<string>();
    const sidSet = new Set<string>();
    let algumFull = false;
    for (const uid of uids) {
      sidSet.add(uid); // sempre: carteira direta por sellerId == uid
      const p = usersById[uid];
      if (!p) continue;
      if (p.codVendSankhya != null && String(p.codVendSankhya) !== '') codSet.add(String(p.codVendSankhya));
      // Acesso-total: carteira = base inteira → só via ÍNDICE (CAMINHO A). NÃO expandir a equipe dele no
      // CAMINHO B: seria centenas de codvends (a força de vendas toda) = a tempestade de queries que o
      // índice existe pra evitar. Sem índice, o acesso-total degrada p/ vazio (aceitável até republicar).
      if (isFullAccess(p.role)) { algumFull = true; continue; }
      (p.equipeSellerIds || []).forEach((s) => sidSet.add(String(s)));
      (p.equipeCodvends || []).forEach((c) => codSet.add(String(c)));
    }
    let cancel = false;
    (async () => {
      // CAMINHO A — acesso-total no mapa: carteira = TODOS os ativos → índice Tier B (com coordenada).
      if (algumFull && scope.full) {
        const idx = await baixarIndiceClientes();
        if (cancel) return;
        if (idx) { setClients(idx.filter((c) => c.location?.latitude != null)); return; }
        // índice indisponível → cai no CAMINHO B (degradado).
      }
      // CAMINHO B — por equipe. Visualizador escopado: a REGRA só prova codvends/sellerIds dentro do
      // escopo dele → filtra (senão UM fora do escopo faz o Firestore recusar a query INTEIRA).
      let cods = Array.from(codSet).filter(Boolean);
      let sids = Array.from(sidSet).filter(Boolean);
      if (!scope.full) {
        const okC = new Set((scope.codvends || []).map(String));
        const okS = new Set((scope.sellerIds || []).map(String));
        cods = cods.filter((c) => okC.has(c));
        sids = sids.filter((s) => okS.has(s));
      }
      const col = collection(db, 'clients');
      const chunk = <T,>(a: T[], n: number): T[][] => { const o: T[][] = []; for (let i = 0; i < a.length; i += n) o.push(a.slice(i, i + n)); return o; };
      const qs: Promise<any>[] = [];
      chunk(sids, 30).forEach((g) => qs.push(getDocs(query(col, where('sellerId', 'in', g)))));
      const codsNum = cods.map(Number).filter((n) => Number.isFinite(n));
      chunk(codsNum, 30).forEach((g) => qs.push(getDocs(query(col, where('sankhya.codvend', 'in', g)))));
      chunk(cods, 30).forEach((g) => qs.push(getDocs(query(col, where('sankhya.codvend', 'in', g)))));
      const snaps = await Promise.allSettled(qs);
      if (cancel) return;
      const byId = new Map<string, Client>();
      snaps.forEach((r) => { if (r.status === 'fulfilled') r.value.forEach((d: any) => byId.set(d.id, { id: d.id, ...d.data() } as Client)); });
      setClients(Array.from(byId.values()).filter((c: any) => c.active !== false));
    })().catch(() => setClients([]));
    return () => { cancel = true; };
  }, [showClients, activeKey, usersById, scope]);

  // Abre a ficha buscando o doc COMPLETO por id — a camada Clientes pode vir do índice Tier B (objeto
  // PARCIAL, sem endereço/cnpj/telefone). Cai no objeto recebido se a leitura falhar.
  const abrirFicha = useCallback((c: Client) => {
    getDoc(doc(db, 'clients', c.id))
      .then((s) => setFicha(s.exists() ? ({ id: s.id, ...s.data() } as Client) : c))
      .catch(() => setFicha(c));
  }, []);

  // Endereço + coordenada ATUAL dos clientes-destino (busca só os que aparecem, com cache).
  // A coordenada atual alimenta o pino do destino (o snapshot destLat/destLng pode estar obsoleto).
  useEffect(() => {
    const ids = Array.from(new Set(locs.map((l) => l.destClientId).filter(Boolean))) as string[];
    ids.forEach((id) => {
      setClientAddr((prev) => {
        if (prev[id] != null) return prev;
        getDoc(doc(db, 'clients', id)).then((s) => {
          if (!s.exists()) return;
          const data = s.data() as any;
          setClientAddr((p) => ({ ...p, [id]: fmtEndereco(data.address) }));
          // O MESMO getDoc já traz `geoPrecision`, `geoCity`, `address.city` e a decisão do servidor
          // (`geoNavegavel`, que manda sobre a regra local) — a confiança sai dele, sem leitura nova.
          setClientLoc((p) => ({
            ...p,
            [id]: temCoordenada(data)
              ? { lat: data.location.latitude, lng: data.location.longitude, confiavel: coordenadaConfiavel(data) }
              : null,
          }));
        });
        return { ...prev, [id]: '' }; // marca como buscando (evita refetch)
      });
    });
  }, [locs]);

  // Carrega as paradas das rotas ativas (p/ mostrar "parada X de N")
  useEffect(() => {
    const ids = Array.from(new Set(locs.map((l) => l.routeId).filter(Boolean))) as string[];
    ids.forEach(async (id) => {
      setRoutes((prev) => {
        if (prev[id]) return prev; // já carregada
        getDoc(doc(db, 'routes', id)).then((s) => {
          if (s.exists()) setRoutes((p) => ({ ...p, [id]: (s.data().stops || []) as RouteStop[] }));
        });
        return prev;
      });
    });
  }, [locs]);

  // Encaixa o trajeto real de cada vendedor nas ruas (map-matching), com cache e throttle.
  // ⚠️ SÓ EM NAVEGAÇÃO. Antes o `trail` só existia nesse modo; desde que o expediente passou a gravar
  // rastro, isto rodaria para TODOS os vendedores o dia inteiro — 13 × 90/h ≈ 1.170 chamadas por HORA
  // de aba aberta, contra uma cota do ORS da ordem de 2.000/dia (e cada aba de gestor tem o seu
  // próprio cache, então dois gestores dobram). No expediente o ganho é quase nulo: a 10s com
  // precisão alta o traçado bruto já acompanha a rua.
  useEffect(() => {
    locs.forEach((l) => {
      if (!l.destClientId) {
        // Fim da navegação: DESCARTA o encaixe. Deixá-lo no cache fazia o painel continuar desenhando
        // a rua da visita anterior, ligada por um segmento reto ao ponto atual do vendedor.
        if (matchedTrailsRef.current[l.sellerId]) setMatchedTrails((p) => { const n = { ...p }; delete n[l.sellerId]; return n; });
        return; // sem destino = expediente puro: não gasta ORS
      }
      const trail = l.trail || [];
      if (trail.length < 3) return;
      const cached = matchedTrailsRef.current[l.sellerId];
      // Chaveado pelo TEMPO do último ponto, não pelo comprimento: o `trail` virou uma JANELA
      // DESLIZANTE de tamanho fixo, então o comprimento fica travado no teto e `length - cached.len`
      // dava sempre 0 — o traçado encaixado congelava para sempre (mesmo bug corrigido no app).
      const ultimoT = Number((trail[trail.length - 1] as any)?.t) || 0;
      if (cached && cached.lastT && ultimoT - cached.lastT < 40000) return; // re-encaixa no máx. a cada 40s
      if (matchInFlight.current.has(l.sellerId)) return;
      matchInFlight.current.add(l.sellerId);
      const lenNow = trail.length;
      httpsCallable(functions, 'mapMatchTrail')({ coordinates: trail.map((p) => ({ lat: p.lat, lng: p.lng })) })
        .then((r: any) => {
          const pts = Array.isArray(r?.data?.trail) ? r.data.trail.map((p: any) => [p.lat, p.lng] as [number, number]) : [];
          setMatchedTrails((prev) => ({ ...prev, [l.sellerId]: { len: lenNow, lastT: ultimoT, destClientId: l.destClientId, pts } }));
        })
        .catch(() => { setMatchedTrails((prev) => ({ ...prev, [l.sellerId]: { len: lenNow, lastT: ultimoT, destClientId: l.destClientId, pts: [] } })); }) // cache negativo: não re-tenta a cada tick
        .finally(() => { matchInFlight.current.delete(l.sellerId); });
    });
  }, [locs]);

  // "parada X de N" a partir da rota + cliente destino atual
  const progresso = (l: LiveLocation): { atual: number; total: number } | null => {
    if (!l.routeId || !routes[l.routeId]) return null;
    const sorted = [...routes[l.routeId]].sort((a, b) => a.order - b.order);
    if (!sorted.length) return null;
    const idx = sorted.findIndex((s) => s.clientId === l.destClientId);
    return { atual: idx >= 0 ? idx + 1 : 0, total: sorted.length };
  };

  const fmtAgo = (d: Date) => {
    const min = Math.round((Date.now() - d.getTime()) / 60000);
    if (min < 1) return 'agora mesmo';
    if (min < 60) return `há ${min} min`;
    if (min < 2880) return `há ${Math.round(min / 60)} h`; // até 48h em horas
    const dias = Math.round(min / 1440);
    return `há ${dias} dia${dias > 1 ? 's' : ''}`;
  };
  const center: [number, number] = locs.length ? [locs[0].lat, locs[0].lng] : [-15.78, -47.93];
  const followed = following ? locs.find((l) => l.sellerId === following) : null;
  const follow = followed ? { lat: followed.lat, lng: followed.lng } : null;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Mapa ao vivo</h1>
          <p style={styles.subtitle}>{locs.length} vendedor(es) em rota agora</p>
        </div>
        <button
          style={{ ...styles.clientsToggle, ...(showClients ? styles.clientsToggleOn : {}) }}
          onClick={() => setShowClients((v) => !v)}
          title="Mostra os clientes dos vendedores que estão em rota agora"
        >
          📍 Clientes {showClients ? '✓' : ''}
        </button>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : locs.length === 0 ? (
        <div style={styles.empty}>Nenhum vendedor com rota ativa no momento.</div>
      ) : (
        <div style={styles.liveWrap}>
          {/* Painel lateral: lista de vendedores ativos — clique centraliza no mapa */}
          <div style={styles.sidePanel}>
            {locs.map((l) => {
              const stale = Date.now() - l.updatedAt.getTime() > 15 * 60000;
              const eta = l.destLat != null ? etaMin(l) : null;
              const mov = !stale ? statusMov(l) : null;
              const desvio = !stale ? desvioMeters(l) : null;
              const foraRota = desvio != null && desvio > 250;
              const alerta = stale || (mov?.parado) || foraRota;
              return (
                <div key={l.sellerId} style={{ ...styles.sideCard, ...(alerta ? styles.sideCardAlert : {}), ...(following === l.sellerId ? styles.sideCardFollow : {}) }} onClick={() => setFocus({ pos: [l.lat, l.lng], n: Date.now() })}>
                  <div style={styles.sideName}>{l.sellerName || 'Vendedor'}</div>
                  <div style={styles.sideMeta}>{l.destClientName ? `🏁 ${l.destClientName}` : 'Sem destino'}</div>
                  {l.destClientId && clientAddr[l.destClientId] ? <div style={styles.sideMeta}>📍 {clientAddr[l.destClientId]}</div> : null}
                  {l.destLat != null && l.destLng != null ? <div style={styles.sideMeta}>GPS: {Number(l.destLat.toFixed(7))}, {Number(l.destLng.toFixed(7))}</div> : null}
                  {(l.startedAt || eta != null) && (
                    <div style={styles.sideMeta}>
                      {l.startedAt ? `🚗 ${fmtHora(l.startedAt)}` : ''}
                      {l.suggestedDurationSeconds != null ? ` · 🕐 ${fmtMin(Math.round(l.suggestedDurationSeconds / 60))}` : ''}
                      {eta != null ? ` · 🏁 ${fmtHora(new Date(Date.now() + eta * 60000))}` : ''}
                    </div>
                  )}
                  <div style={styles.sideMeta}>
                    {eta != null ? `⏱️ ${fmtEta(eta)}` : ''}
                    {stale ? ' · ⚠️ sinal antigo' : mov ? (mov.parado ? ` · ⏸️ parado ${mov.min}min` : ' · ▶️ em movimento') : ''}
                    {foraRota ? ' · ⚠️ fora da rota' : ''}
                  </div>
                  <button
                    style={{ ...styles.followBtn, ...(following === l.sellerId ? styles.followBtnOn : {}) }}
                    onClick={(e) => { e.stopPropagation(); setFollowing(following === l.sellerId ? null : l.sellerId); }}
                  >
                    {following === l.sellerId ? '🎯 Seguindo' : '🎯 Seguir'}
                  </button>
                </div>
              );
            })}
          </div>

          <div style={styles.mapWrapper}>
          <MapContainer center={center} zoom={11} style={{ height: 600, width: '100%', borderRadius: 12 }}>
            <OsmTiles />
            <MapController focus={focus} follow={follow} />
            <MapProbe onMap={setMapObj} onZoom={setZoomLvl} />
            {showClients && <ClientPins clients={clients} onSelect={abrirFicha} />}
            {locs.map((l) => {
              const stale = Date.now() - l.updatedAt.getTime() > 15 * 60000;
              const eta = l.destLat != null ? etaMin(l) : null;
              const rem = l.destLat != null ? remainingMeters(l) : null;
              const chegada = eta != null ? fmtHora(new Date(Date.now() + eta * 60000)) : null;
              const prog = progresso(l);
              const mov = !stale ? statusMov(l) : null;
              const desvio = !stale ? desvioMeters(l) : null;
              const foraRota = desvio != null && desvio > 250;
              const sugg = (l.suggestedRoute || []).map((p) => [p.lat, p.lng] as [number, number]);
              const rawArr = l.trail || [];
              const rawTrail = rawArr.map((p) => [p.lat, p.lng] as [number, number]);
              if (l.lat != null && l.lng != null) rawTrail.push([l.lat, l.lng]);
              // Usa o encaixe só se corresponde ao trajeto atual (não a uma rota anterior mais longa).
              const cache = matchedTrails[l.sellerId];
              // Só vale para a navegação a que pertence: sem esta amarra, terminada a visita o encaixe antigo
              // ficava desenhado o resto do dia (o efeito não roda mais no expediente para corrigi-lo).
              const useMatched = !!cache && cache.pts.length > 1 && cache.destClientId === l.destClientId;
              let trail: [number, number][];
              if (useMatched && cache) {
                // Cauda por TEMPO, não por indice: com a janela deslizante o  deixou de ser um
                // deslocamento valido dentro do array (o inicio some conforme entram pontos novos).
                const tail = rawArr.filter((p) => Number((p as any).t) > (cache!.lastT || 0)).map((p) => [p.lat, p.lng] as [number, number]);
                trail = [...cache.pts, ...tail];
                if (l.lat != null && l.lng != null) trail.push([l.lat, l.lng]);
              } else {
                trail = rawTrail;
              }
              return (
                <React.Fragment key={l.sellerId}>
                  {/* Rota sugerida (menor percurso por rua) — azul tracejado */}
                  {sugg.length > 1 && (
                    <Polyline positions={sugg} pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.6, dashArray: '8 8' }} />
                  )}
                  {/* Trajeto real do vendedor — laranja. Quebrado em saltos grandes de GPS (não liga reto
                      dois pontos distantes → sem a diagonal falsa cruzando mata quando o rastro ficou esparso). */}
                  {segmentarTrajeto(trail).map((seg, i) => seg.length > 1 && (
                    <Polyline key={`trail-${l.sellerId}-${i}`} positions={seg} pathOptions={{ color: '#f5821f', weight: 4, opacity: 0.9 }} />
                  ))}
                  {/* Destino (cliente) */}
                  {l.destLat != null && l.destLng != null && (
                    <CircleMarker center={[l.destLat, l.destLng]} radius={8} pathOptions={{ color: '#fff', weight: 2, fillColor: '#dc2626', fillOpacity: 1 }}>
                      <Tooltip direction="top">🏁 {l.destClientName || 'Cliente'}{eta != null ? ` • ${fmtEta(eta)}` : ''}</Tooltip>
                    </CircleMarker>
                  )}
                  {/* Posição atual do vendedor — DESLIZA em vez de saltar (ver AnimatedCircleMarker) */}
                  <AnimatedCircleMarker
                    lat={l.lat}
                    lng={l.lng}
                    radius={10}
                    pathOptions={{ color: '#fff', weight: 2, fillColor: stale ? '#9ca3af' : '#16a34a', fillOpacity: 0.9 }}
                  >
                    <Tooltip permanent direction={fan.dir.get(l.sellerId) ?? 'top'}>{l.sellerName || 'Vendedor'}{eta != null && !stale ? ` • ${fmtEta(eta)}` : ''}</Tooltip>
                    <Popup>
                      <strong>{l.sellerName || 'Vendedor'}</strong>
                      {prog && prog.atual > 0 && <span style={{ color: '#666' }}> · parada {prog.atual} de {prog.total}</span>}<br />
                      {l.destClientName ? (
                        <>🏁 Indo para: <strong>{l.destClientName}</strong><br /></>
                      ) : null}
                      {l.destClientId && clientAddr[l.destClientId] ? <>📍 {clientAddr[l.destClientId]}<br /></> : null}
                      {l.destLat != null && l.destLng != null ? <>GPS: {Number(l.destLat.toFixed(7))}, {Number(l.destLng.toFixed(7))}<br /></> : null}
                      {eta != null && (
                        <>⏱️ {fmtEta(eta)}{rem != null ? ` · ${fmtKm(rem)}` : ''}{chegada ? ` · chega ${chegada}` : ''}<br /></>
                      )}
                      {l.startedAt && <>🚗 Saiu {fmtHora(l.startedAt)}{l.suggestedDurationSeconds != null ? ` · percurso ~${fmtMin(Math.round(l.suggestedDurationSeconds / 60))}` : ''}<br /></>}
                      {mov && (() => { const v = speedKmh(l); return (
                        <span style={{ color: mov.parado ? '#b45309' : '#16a34a' }}>
                          {mov.parado ? `⏸️ Parado há ${mov.min} min` : `▶️ Em movimento${v != null ? ` · ${Math.round(v)} km/h` : ''}`}<br />
                        </span>
                      ); })()}
                      {foraRota && <><span style={{ color: '#b45309' }}>⚠️ Fora da rota sugerida ({fmtKm(desvio)})</span><br /></>}
                      {phones[l.sellerId] && (
                        <span style={{ display: 'inline-flex', gap: 8, margin: '4px 0' }}>
                          <a href={`tel:${soDigitos(phones[l.sellerId])}`} style={styles.contactBtn}>📞 Ligar</a>
                          <a href={`https://wa.me/55${soDigitos(phones[l.sellerId]).replace(/^0+/, '')}`} target="_blank" rel="noreferrer" style={styles.contactBtn}>💬 WhatsApp</a>
                        </span>
                      )}
                      {phones[l.sellerId] && <br />}
                      <span style={{ color: '#888', fontSize: 12 }}>Atualizado {fmtAgo(l.updatedAt)} · {l.lat.toFixed(5)}, {l.lng.toFixed(5)}</span>
                      {stale && <><br /><span style={{ color: '#dc2626' }}>⚠️ sinal antigo</span></>}
                    </Popup>
                  </AnimatedCircleMarker>
                  {/* Seta de direção (para onde o vendedor está indo) */}
                  {!stale && (() => { const h = headingOf(l); return h != null ? (
                    <Marker position={[l.lat, l.lng]} icon={arrowIcon(h)} interactive={false} />
                  ) : null; })()}
                </React.Fragment>
              );
            })}
          </MapContainer>
          <div style={styles.legend}>
            <span><span style={{ ...styles.swatch, background: '#2563eb' }} /> Rota sugerida (menor percurso)</span>
            <span><span style={{ ...styles.swatch, background: '#f5821f' }} /> Trajeto real do vendedor</span>
            <span><span style={{ ...styles.swatch, background: '#dc2626', borderRadius: '50%' }} /> Cliente (destino)</span>
            {showClients && Object.values(CLIENT_STATUS).map((s) => (
              <span key={s.color}><span style={{ ...styles.swatch, background: s.color, borderRadius: '50%' }} /> {s.label}</span>
            ))}
          </div>
          </div>
        </div>
      )}

      {ficha && <ClientFormModal client={ficha} onClose={() => setFicha(null)} onSaved={() => setFicha(null)} />}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' },
  clientsToggle: { border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: 8, padding: '9px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  clientsToggleOn: { background: '#f5821f', color: '#fff', borderColor: '#f5821f' },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  empty: { background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', color: '#999', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  liveWrap: { display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' },
  sidePanel: { width: 280, maxHeight: 600, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 },
  sideCard: { background: '#fff', borderRadius: 10, padding: '10px 12px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', cursor: 'pointer', borderLeft: '4px solid #16a34a' },
  sideCardAlert: { borderLeft: '4px solid #f59e0b' },
  sideCardFollow: { boxShadow: '0 0 0 2px #0f766e' },
  followBtn: { marginTop: 8, background: '#f0fdfa', color: '#0f766e', border: '1px solid #99f6e4', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%' },
  followBtnOn: { background: '#0f766e', color: '#fff', borderColor: '#0f766e' },
  sideName: { fontWeight: 700, color: '#1a1a2e', fontSize: 14 },
  sideMeta: { fontSize: 12, color: '#666', marginTop: 2 },
  contactBtn: { textDecoration: 'none', background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 600 },
  mapWrapper: { flex: 1, minWidth: 320, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 12px rgba(0,0,0,0.08)' },
  legend: { display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12, fontSize: 13, color: '#444' },
  swatch: { display: 'inline-block', width: 14, height: 4, marginRight: 6, verticalAlign: 'middle' },
};
